import {saveUserLS} from '@asocial/fe-utils'
import {useMediaQuery} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import moment from 'moment/moment'
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {LocationSelect} from 'entities/inputs'
import {PhotoTypes} from 'entities/messages'
import {CountChildrenList, DrinkingList, EducationList, MartialStatusList, SmokingList} from 'entities/profile'
import {ChangeProfileInfo, setUser, useUser} from 'entities/user'
import {interestsOpenModal} from 'entities/user/model/interests'
import {Gallery} from 'features/gallery'
import {Interests} from 'features/interests'
import {SelectWithLabel} from 'features/selectWithLabel'
import {convertNullToString, deepEqual, mediaVariants, names} from 'shared/lib'
import {HeightList, ProfileType, WeightList} from 'shared/types'
import {Button} from 'shared/ui/buttons'
import {DateField} from 'shared/ui/datePicker'
import {Plus} from 'shared/ui/icons'
import defaultBanner from 'shared/ui/img/default-banner.jpg'
import {Loader} from 'shared/ui/loader'
import {Ref} from 'shared/ui/ref'
import {InputContainerTextField, TextField} from 'shared/ui/textField'
import {AvatarBox} from 'widgets/avatarBox'
import {EditBannerButton} from 'widgets/buttons'
import {InterestsModal} from 'widgets/interestsModal'
import styles from './styles.module.scss'

export const EditProfile: FC = () => {
  const dispatch = useDispatch()
  const user = useUser()
  const [fields, setFields] = useState(user.profile)
  const [errors, setErrors] = useState({name: '', dateBirth: ''})
  const [isLoading, setIsLoading] = useState(false)
  const [isChangedFields, setIsChangedFields] = useState(false)
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)

  const updateUser = (profile: ProfileType) => {
    dispatch(setUser({...user, profile}))
    saveUserLS({...user, profile})
  }

  const initialFieldWithEmpty = useMemo(() => {
    const initialField = Object.entries(user.profile)
    return convertNullToString(initialField)
  }, [user.profile])

  const currentFieldsWithEmpty = useMemo(() => {
    const currentFields = Object.entries(fields)
    return convertNullToString(currentFields)
  }, [fields])


  useEffect(() => {
    if (deepEqual(initialFieldWithEmpty, currentFieldsWithEmpty)) {
      return setIsChangedFields(false)
    }
    return setIsChangedFields(true)
  }, [fields])


  const onChangeFieldHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'childrenCount') {
      return setFields((prevState) => ({...prevState, [e.target.name]: Number(e.target.value)}))
    }
    setFields((prevState) => ({...prevState, [e.target.name]: e.target.value}))
  }

  const cancelHandler = () => setFields(user.profile)

  const saveHandler = () => {
    if (!fields.name) return setErrors({...errors, name: 'Empty Name'})
    if (!fields.dateBirth)
      return setErrors({...errors, dateBirth: 'Empty Birthday'})
    if (errors.dateBirth) return
    setIsLoading(true)

    ChangeProfileInfo({
      name: fields.name,
      dateBirth: fields.dateBirth,
      city: fields.city,
      country: fields.country,
      maritalStatus: fields.maritalStatus,
      childrenCount: Number(fields.childrenCount),
      education: fields.education,
      occupation: fields.occupation,
      height: fields.height,
      weight: fields.weight,
      drinking: fields.drinking,
      smoking: fields.smoking,
      about: fields.about
    }).then(({data: user}) => {
      updateUser(user)
      setIsLoading(false)
      setIsChangedFields(false)
    })
  }

  return (
    <div className={styles.editProfile}>
      <div className={`${styles.profile} ${styles.customScroll}`}>
        <div className={styles.banner}>
          <div className={styles.bannerImage}>
            <img
              src={
                user.profile.bannerUrl
                  ? user.profile.bannerUrl + mediaVariants.banner
                  : defaultBanner
              }
              alt='banner'
            />
          </div>
          <div className={styles.editBanner}>
            <EditBannerButton className={styles.editBannerButton} />
          </div>
          <div className={styles.avatarWrapper}>
            <AvatarBox
              canEdit
              src={user.profile.avatarUrl || ''}
              type={PhotoTypes.avatar}
              alt={user.profile.name}
              showEdit
              hideStatus
            />
          </div>
        </div>
        <div className={styles.fields}>
          <TextField
            name='name'
            label='Name or nickname'
            placeholder='Alex'
            value={fields.name}
            error={errors.name}
            required
            onChange={(event) => {
              setErrors({...errors, name: ''})
              onChangeFieldHandler(event)
            }}
          />
          <InputContainerTextField
            label='Date of birth'
            required
            error={errors.dateBirth}
          >
            <DateField
              minDate={moment('1945-01-01')}
              maxDate={moment().subtract(18, 'years')}
              value={moment(fields.dateBirth)}
              onChange={(newValue) =>
                setFields((prevState) => ({
                  ...prevState,
                  dateBirth: newValue ? newValue.format('YYYY-MM-DD') : ''
                }))
              }
              onError={(newError) =>
                setErrors({
                  ...errors,
                  dateBirth: newError ? 'Invalid Date' : ''
                })
              }
            />
          </InputContainerTextField>
          <SelectWithLabel
            name='education'
            label='Education'
            placeholder='Not specified'
            value={fields.education}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {EducationList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <TextField
            name='occupation'
            label='Occupation'
            placeholder='Business owner'
            value={fields.occupation || ''}
            onChange={onChangeFieldHandler}
            maxLength={30}
          />
          <div className={styles.interests}>
            <label className={styles.formLabel}>Interests</label>
            {user.profile.interests.length ?
              <>
                <Interests data={user.profile.interests} />
                <Ref onClick={() => dispatch(interestsOpenModal())} asDiv>
                  Edit interests
                </Ref>
              </>
              :
              <Button
                styleType='primary'
                onClick={() => dispatch(interestsOpenModal())}
                className={styles.interestsBtnAdd}
              >
                <Plus />
                Add
              </Button>
            }
            <InterestsModal />
          </div>
          <SelectWithLabel
            name='maritalStatus'
            label='Marital status'
            placeholder='Not specified'
            value={fields.maritalStatus}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {MartialStatusList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <InputContainerTextField label='Country' required>
            <LocationSelect
              className={styles.countrySelect}
              placeholder=''
              name='country'
              value={fields.country}
              onChange={(e) =>
                onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
              }
            />
          </InputContainerTextField>
          <TextField
            name='city'
            label='City'
            placeholder='City'
            value={fields.city || ''}
            onChange={onChangeFieldHandler}
          />
          <SelectWithLabel
            name='height'
            label='Height'
            placeholder='Not specified'
            value={fields.height}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {HeightList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            name='weight'
            label='Weight'
            placeholder='Not specified'
            value={fields.weight}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {WeightList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            name='drinking'
            label='Drinking'
            placeholder='Not specified'
            value={fields.drinking}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {DrinkingList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            name='smoking'
            label='Smoking'
            placeholder='Not specified'
            value={fields.smoking}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {SmokingList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            name='childrenCount'
            label='Children'
            placeholder='Not specified'
            value={fields.childrenCount}
            onChange={(e) =>
              onChangeFieldHandler(e as ChangeEvent<HTMLInputElement>)
            }
          >
            {CountChildrenList.map((item) => (
              <MenuItem key={item.id} value={item.id.toString()}>
                {item.label}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <TextField
            name='about'
            label='About'
            placeholder='Text'
            multiline
            inputContainerStyles={{height: 132}}
            onChange={onChangeFieldHandler}
            value={fields.about || ''}
            withCounter
            maxLength={1000}
          />
        </div>
      </div>
      {!is768p || isChangedFields ? <div className={styles.btnContainer}>
        <Button
          styleType='secondary'
          onClick={cancelHandler}
          className={names(styles.btn, styles.btnCancel)}
          isDisabled={isLoading || !isChangedFields}
        >
          Cancel
        </Button>
        <Button
          styleType='primary'
          onClick={saveHandler}
          className={`${styles.btn} ${isLoading ? styles.btnWithLoader : ''}`}
          isDisabled={!isChangedFields}
        >
          {isLoading ? <Loader className={styles.loader} innerColor='white' /> : 'Save'}
        </Button>
      </div> : null}
      <Gallery />
    </div>
  )
}
